import { useEffect, useRef, useState, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { MessageBubble } from './MessageBubble';
import { Spinner } from '@/components/ui/Spinner';
import type { OptimisticMessage } from '@/hooks/useMessages';

interface MessageTimelineProps {
  messages: OptimisticMessage[];
  isLoading: boolean;
  hasMore: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  onRetryMessage?: (idempotencyKey: string, text: string) => void;
}

/**
 * Estimated height for each message (will be measured dynamically)
 */
const ESTIMATED_MESSAGE_HEIGHT = 80;

/**
 * Threshold for considering user "at bottom" (px from bottom)
 */
const SCROLL_BOTTOM_THRESHOLD = 100;

export function MessageTimeline({
  messages,
  isLoading,
  hasMore,
  fetchNextPage,
  isFetchingNextPage,
  onRetryMessage,
}: MessageTimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const prevMessageCountRef = useRef(messages.length);

  // Virtual list configuration
  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => ESTIMATED_MESSAGE_HEIGHT,
    overscan: 5, // Render 5 extra items above/below viewport
    getItemKey: (index) => messages[index]?.id ?? index,
  });

  // Track scroll position for "at bottom" detection
  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const atBottom = distanceFromBottom < SCROLL_BOTTOM_THRESHOLD;

    setIsAtBottom(atBottom);
    if (atBottom) {
      setNewMessageCount(0);
    }
  }, []);

  // Auto-scroll when new messages arrive (if user is at bottom)
  useEffect(() => {
    const newCount = messages.length - prevMessageCountRef.current;
    if (newCount > 0) {
      if (isAtBottom) {
        // Scroll to last item
        virtualizer.scrollToIndex(messages.length - 1, { align: 'end' });
      } else {
        setNewMessageCount((prev) => prev + newCount);
      }
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length, isAtBottom, virtualizer]);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (!isLoading && messages.length > 0 && prevMessageCountRef.current === 0) {
      virtualizer.scrollToIndex(messages.length - 1, { align: 'end' });
      prevMessageCountRef.current = messages.length;
    }
  }, [isLoading, messages.length, virtualizer]);

  const scrollToBottom = useCallback(() => {
    virtualizer.scrollToIndex(messages.length - 1, { align: 'end', behavior: 'smooth' });
    setNewMessageCount(0);
  }, [virtualizer, messages.length]);

  if (isLoading && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-500">
        No messages yet. Start the conversation!
      </div>
    );
  }

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div className="flex-1 relative overflow-hidden">
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="h-full overflow-y-auto px-4 py-3 bg-gray-50"
      >
        {/* Load older messages button */}
        {hasMore && (
          <div className="flex justify-center mb-3">
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="text-xs text-gray-500 hover:text-gray-700 disabled:text-gray-400 px-3 py-1 bg-white rounded-full border border-gray-200"
            >
              {isFetchingNextPage ? <Spinner size="sm" /> : 'Load older messages'}
            </button>
          </div>
        )}

        {/* Virtualized message list */}
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualItems.map((virtualItem) => {
            const message = messages[virtualItem.index];
            if (!message) return null;

            const showRetry =
              message._status === 'failed' && message._idempotencyKey && onRetryMessage;

            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <MessageBubble
                  message={message}
                  {...(showRetry && {
                    onRetry: () => onRetryMessage(message._idempotencyKey!, message.sanitized_text),
                  })}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* New messages indicator */}
      {newMessageCount > 0 && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-full shadow-lg hover:bg-blue-700 transition-colors z-10"
        >
          {newMessageCount} new message{newMessageCount > 1 ? 's' : ''} ↓
        </button>
      )}
    </div>
  );
}
